import React, { useContext, useState } from 'react'
import * as XLSX from 'xlsx'
import dayjs from 'dayjs'

import {
    Flag,
    ICheckedDetailOrder,
    IDetailTodo,
    IInvoiceBody,
    TabType,
} from 'interfaces/live.interface'
import { phoneHypen } from 'utils/lib'
import { getDetailTodosForExcel, pathInvoiceBulk } from 'api/live.request'
import { ALL_TYPE, EXCEL_COLUMN, EXCEL_NAME, STATUS_MAP } from '../constants/live.constant'
import { getBroadcast, getInvoiceCompanyList } from 'api/broadcast.request'
import { useMutation } from 'react-query'
import { getShippingFee } from 'utils/tossDetailHandler'

interface Props {
    tab: TabType
    checked: string[]
    checkedOrder: { [key: string]: ICheckedDetailOrder }
    flag: Flag
    updateItem: IDetailTodo | null
    isResetFlag: boolean
    updateInvoiceItem: { guestId: number; body: IInvoiceBody | null } | null
    isOpenShippingInfo: boolean
    isOpenInvoiceInfo: boolean

    setTab: (type: TabType) => void
    setChecked: (orders: ICheckedDetailOrder[], checked?: boolean) => void

    onAllChecked: (orders: ICheckedDetailOrder[], checked?: boolean) => void
    onFlag: (reason: string, option?: any) => void
    onExportExcel: (broadcastId?: string) => Promise<boolean>
    onOpenShpiingInfo: (orders: IDetailTodo) => void
    onCloseShippingInfo: () => void

    onOpenInvoiceInfo: (guestId: number, invoice: IInvoiceBody | null) => void
    onCloseInvoiceInfo: () => void

    onInvoiceExcelUpload: (
        file: File,
        broadcastId?: string
    ) => Promise<{ isSuccess: boolean; message?: string | React.ReactNode }>
}

const DetailContext = React.createContext<Props>(null!)

export const useDetail = () => {
    const context = useContext(DetailContext)
    return context
}

export default function DetailProvider({ children }: React.PropsWithChildren<{}>) {
    // option
    const [isResetFlag, setIsResetFlag] = useState<boolean>(false)
    const [flag, setFlag] = useState<Flag>({
        status: false,
        reason: null,
        option: null,
    })
    const [tab, setTab] = useState<TabType>('ORDER')
    const [checked, setChecked] = useState<{
        [key: string]: ICheckedDetailOrder
    }>({})

    const [isOpenShippingInfo, setIsOpenShippingInfo] = useState(false)
    const [updateItem, setUpdateItem] = useState<IDetailTodo | null>(null)

    const [isOpenInvoiceInfo, setIsOpenInvoiceInfo] = useState(false)
    const [updateInvoiceItem, setUpdateInvoiceItem] =
        useState<{ guestId: number; body: IInvoiceBody | null } | null>(null)

    const invoiceUploadMutation = useMutation(pathInvoiceBulk)

    // Set Middleware
    const _setTab = (tab: TabType) => {
        setTab(tab)
        setChecked({})
    }

    const _setChecked = (orders: ICheckedDetailOrder[], checked?: boolean) => {
        setChecked((prevState) => {
            const copy = { ...prevState }

            if (checked === true) {
                for (let i = 0; i < orders.length; i++) {
                    const order = orders[i]
                    copy[order.id] = order
                }
            } else if (checked === false) {
                for (let i = 0; i < orders.length; i++) {
                    const order = orders[i]
                    delete copy[order.id]
                }
            }

            return copy
        })
    }

    // Handler Event

    //
    const onAllChecked = (orders: ICheckedDetailOrder[], checked?: boolean) => {
        if (checked) {
            const _orders: { [key: string]: ICheckedDetailOrder } = {}

            for (let i = 0; i < orders.length; i++) {
                _orders[orders[i].id.toString()] = orders[i]
            }

            setChecked(_orders)
        } else {
            setChecked({})
        }
    }

    //
    const onFlag = (reason: string, option: any = null) => {
        setFlag((prevState) => {
            return {
                status: !prevState.status,
                reason,
                option,
            }
        })

        setTimeout(() => {
            setFlag({ status: false, reason: null, option: null })
        }, 1000)
    }

    /**
     *
     */
    const onExportExcel = async (broadcastId?: string) => {
        if (!broadcastId) return false

        const filename = EXCEL_NAME[tab]
        const status = tab === 'ALL' ? ALL_TYPE : tab
        const info = await getBroadcast(broadcastId)
        let infoData = info.data.data
        const response = await getDetailTodosForExcel(broadcastId, status)
        let data = response.data.data
        // console.log('excel', data, infoData)

        if (!data || data.length === 0 || !infoData) return false

        let headers = Object.values(EXCEL_COLUMN)
        let columns = Object.keys(EXCEL_COLUMN)

        if (tab === 'DELIVERY') {
            headers.push('배송일시')
            columns.push('statusUpdatedAt')
        } else if (tab === 'ALL') {
            headers = ['주문일시', '주문상태', '상태변경일', ...headers]
            columns = ['createdAt', 'status', 'statusUpdatedAt', ...columns]
        } else if (tab === 'CANCEL') {
            headers = [
                '주문자명',
                '유저 식별번호',
                '상품명',
                '상품번호',
                '옵션',
                '수량',
                '취소사유',
                '취소일시',
            ]
            columns = [
                'nickname',
                'guestId',
                'productName',
                'number',
                'productOption',
                'count',
                'cancelReason',
                'statusUpdatedAt',
            ]
        }

        // 총 금액 컬럼 추가
        if (
            tab === 'ORDER' ||
            tab === 'DEPOSIT' ||
            tab === 'DELIVERY_READY' ||
            tab === 'DELIVERY' ||
            tab === 'ALL'
        ) {
            const shippingFeeIdx = headers.findIndex((header) => header === '총 금액')
            if (shippingFeeIdx !== -1) {
                headers.splice(shippingFeeIdx + 1, 0, '배송비', '제주/도서산간 배송비')
                columns.splice(shippingFeeIdx + 1, 0, 'shippingFee', 'remoteFee')
            }
        }

        let rows: (string | number)[][] = [headers]

        // 배송비, 총 금액 계산
        // 전체 탭이 들어가게 됨으로서 취소, 환불 상태인 경우는 제외 해야한다.
        const FeeCal: any = {}

        for (let i = 0; i < data.length; i++) {
            const d = data[i]
            if (FeeCal[d.nickname]) {
                FeeCal[d.nickname].push(d)
            } else {
                FeeCal[d.nickname] = [d]
            }
            const row: (string | number)[] = []
            // console.log(data[i])

            const defaultAddress = !d.newAddress ? d.address : d.newAddress
            const detailAddress = d.newAddressDetail ?? '-'

            for (let j = 0; j < columns.length; j++) {
                const column = columns[j]
                const value = d[column as keyof typeof d]
                let _value: string | number =
                    value !== null && value !== undefined ? String(value) : '-'
                if (column === 'allAddress') {
                    _value = `${defaultAddress} ${detailAddress}`
                } else if (column === 'address') {
                    _value = defaultAddress
                } else if (column === 'detailAddress') {
                    _value = detailAddress
                } else if (column === 'phone' || column === 'shippingPhone') {
                    // 핸드폰번호 처리
                    _value = phoneHypen(_value)
                } else if (column === 'createdAt' || column === 'updatedAt') {
                    // 날짜 처리
                    _value = dayjs(_value).format('YYYY-MM-DD HH:mm:ss')
                } else if (column === 'status') {
                    // 상태 처리
                    _value = STATUS_MAP[_value]
                } else if (column === 'price' || column === 'count' || column === 'totalPrice') {
                    const num = Number(_value)
                    _value = isNaN(num) ? '0' : num
                } else if (column === 'totalPayment') {
                    const num = Number(_value)
                } else if (column === 'statusUpdatedAt') {
                    // console.log(_value)
                    _value =
                        _value && _value !== '-' ? dayjs(_value).format('YYYY-MM-DD HH:mm:ss') : '-'
                }

                row.push(_value)
            }
            rows.push(row)
        }

        // console.log(FeeCal, rows)

        // '총 결제금액' 컬럼 인덱스 찾기
        const totalPaymentIdx = rows[0].findIndex((header) => header === '총 결제금액')
        const nicknameIdx = rows[0].findIndex((header) => header === '주문자명')
        const shippingFee = rows[0].findIndex((header) => header === '배송비')
        const rFee = rows[0].findIndex((header) => header === '제주/도서산간 배송비')
        const statusIdx = rows[0].findIndex((header) => header === '주문상태')

        Object.keys(FeeCal).map((e) => {
            const nick = e
            const arr = FeeCal[nick]
            console.log(arr)
            let totalPrice = 0

            let remoteFee = 0
            let jejuFee = 0

            arr.map((j: any) => {
                const itemStatus = j.status
                if (tab === 'ALL' && (itemStatus === 'CANCEL' || itemStatus === 'REFUND')) return
                const p = j.price * j.count
                totalPrice += p

                if (remoteFee === 0) {
                    remoteFee = j.remoteAreaShippingCharge ?? 0
                }
                if (jejuFee === 0) {
                    jejuFee = j.jejuShippingCharge ?? 0
                }
            })

            // shippingFee 게산
            const fee = getShippingFee({
                shippingType: infoData.shippingType,
                shippingPurchaseAmount: infoData.shippingPurchaseAmount,
                shippingCharge: infoData.shippingCharge,
                totalProductPrice: totalPrice,
                remoteAreaShippingCharge: remoteFee,
                jejuShippingCharge: jejuFee,
                isTotal: false,
            })

            const allFee = getShippingFee({
                shippingType: infoData.shippingType,
                shippingPurchaseAmount: infoData.shippingPurchaseAmount,
                shippingCharge: infoData.shippingCharge,
                totalProductPrice: totalPrice,
                remoteAreaShippingCharge: remoteFee,
                jejuShippingCharge: jejuFee,
                isTotal: true,
            })

            // rows[1]번부터 rows의 닉네임이 nick인 row의 '총 결제금액' 값에 fee 추가
            if (totalPaymentIdx !== -1 && nicknameIdx !== -1) {
                rows = rows.map((p) => {
                    if (p[nicknameIdx] === nick) {
                        let _ = p
                        _[totalPaymentIdx] = Number(p[totalPaymentIdx]) + Number(allFee)
                        _[shippingFee] =
                            _[statusIdx] === '주문취소' || _[statusIdx] === '환불처리'
                                ? 0
                                : Number(fee)
                        _[rFee] =
                            _[statusIdx] === '주문취소' || _[statusIdx] === '환불처리'
                                ? 0
                                : Number(remoteFee + jejuFee)
                        return _
                    } else {
                        return p
                    }
                })
            }
        })

        // console.log(rows)

        // 미입금, 입금확인 컬럼에서 배송사, 송장번호 컬럼 제거
        if (tab === 'ORDER' || tab === 'DEPOSIT') {
            // 배송사 인덱스 rows[0] 에서 찾기
            const deliveryCompanyIdx = rows[0].findIndex((header) => header === '택배사')
            // 헤더 컬럼 제거
            if (deliveryCompanyIdx !== -1) {
                rows.forEach((row) => {
                    row.splice(deliveryCompanyIdx, 1)
                })
            }
            // 송장번호 인덱스
            const invoiceNumberIdx = rows[0].findIndex((header) => header === '송장번호')
            // 헤더 컬럼 제거
            if (invoiceNumberIdx !== -1) {
                rows.forEach((row) => {
                    row.splice(invoiceNumberIdx, 1)
                })
            }
        }

        const sheet = XLSX.utils.aoa_to_sheet(rows)
        const book = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(book, sheet, 'Sheet1')
        XLSX.writeFile(book, filename)
        return true
    }

    const onInvoiceExcelUpload = async (file: File, broadcastId?: string) => {
        if (!broadcastId)
            return {
                isSuccess: false,
                message: '방송 아이디가 없습니다.',
            }

        const AllowDeliveryCompany = await getInvoiceCompanyList()
        const companies = AllowDeliveryCompany.data.data?.map((e) => e.name)
        if (companies?.length === 0) {
            return {
                isSuccess: false,
                message: '등록된 택배사가 없습니다.',
            }
        }

        const orderCodeList = await getDetailTodosForExcel(broadcastId, ALL_TYPE)
        const orderCodeListData = orderCodeList.data.data?.map((e) => e.orderCode)
        if (!orderCodeListData || orderCodeListData.length === 0) {
            return {
                isSuccess: false,
                message: '주문 내역이 존재하지 않습니다.',
            }
        }

        // 주문 코드 중복 제거
        const uniqueOrderCodeList = Array.from(new Set(orderCodeListData))
        // console.log(uniqueOrderCodeList);

        return new Promise<{
            isSuccess: boolean
            message?: string | React.ReactNode
        }>((resolve) => {
            const reader = new FileReader()
            reader.readAsBinaryString(file)
            console.log(4)
            reader.onload = async (e) => {
                const data: string | ArrayBuffer | null | undefined = e.target?.result
                const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'binary' })
                const sheetName: string = workbook.SheetNames[0]
                const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName]
                const jsonData: (string | number | undefined)[][] = XLSX.utils.sheet_to_json(
                    worksheet,
                    { header: 1, raw: true }
                )

                // const headers: string[] = jsonData[0] as string[];
                // const desiredColumns: string[] = ["주문코드", "택배사", "송장번호"];

                const desiredIndexes: number[] = [0, 1, 2] // 예: 1열, 2열, 3열 가져오기

                const rows: (string | number | undefined)[][] = jsonData
                    .slice(1)
                    .filter((row) => row.length > 0)

                const parsed: string[][] = rows.map((row) =>
                    desiredIndexes.map((i) => String(row[i] ?? ''))
                )

                // console.log(parsed);

                const filteredParsed: string[][] = parsed.filter((row) =>
                    row.some((cell) => cell.trim() !== '')
                )

                if (filteredParsed.length === 0) {
                    resolve({
                        isSuccess: false,
                        message: '엑셀 데이터가 없습니다.',
                    })
                    return
                }

                // 1. 주문코드만 추출
                const orderCodes = filteredParsed.map((row) => row[0]) // row[0] = orderCode

                // 2. 중복된 주문코드의 인덱스(줄 번호) 추출
                const seen: Record<string, number[]> = {}

                orderCodes.forEach((code, i) => {
                    if (!seen[code]) {
                        seen[code] = [i + 2] // 엑셀 줄 번호 (헤더 제외 후 1부터 시작)
                    } else {
                        seen[code].push(i + 2)
                    }
                })

                const duplicates = Object.entries(seen)
                    .filter(([_, lines]) => lines.length > 1)
                    .map(([code, lines]) => ({ orderCode: code, lines }))

                let errTotalCount = 0
                if (duplicates.length > 0) {
                    resolve({
                        isSuccess: false,
                        message: (
                            <>
                                <div>주문코드가 중복 입력되었습니다.</div>
                                <div>하나의 주문코드에는 송장번호 1개만 등록하실 수 있습니다.</div>
                                {duplicates.map((dup) => {
                                    errTotalCount += 1
                                    if (errTotalCount > 10) return null
                                    return (
                                        <div className="text-accect" key={dup.orderCode}>
                                            {dup.orderCode} {' > '}
                                            {`${dup.lines.join('번 ')}번째 줄을 확인해주세요.`}
                                        </div>
                                    )
                                })}
                                {errTotalCount > 10 && (
                                    <div className="text-accect">
                                        ... {errTotalCount}개 중복된 주문코드가 있습니다.
                                    </div>
                                )}
                            </>
                        ),
                    })
                    return
                }
                console.log(5)
                const reqData = filteredParsed.map((row) => {
                    const [orderCode, deliveryCompany, invoiceNumber] = row
                    return {
                        orderCode,
                        deliveryCompany,
                        invoiceNumber,
                    }
                })

                // 택배사, 주문코드 검증
                reqData.map((e, i) => {
                    const { orderCode, deliveryCompany, invoiceNumber } = e
                    if (!companies?.includes(deliveryCompany)) {
                        resolve({
                            isSuccess: false,
                            message: (
                                <>
                                    <div>택배사는 {companies?.join(', ')} 중 하나여야 합니다.</div>
                                    <div className="text-accect">
                                        {i + 2}번째 줄을 확인해주세요.
                                    </div>
                                </>
                            ),
                        })
                        return
                    }

                    if (!uniqueOrderCodeList.includes(orderCode)) {
                        resolve({
                            isSuccess: false,
                            message: (
                                <>
                                    <div>존재하지 않거나 잘못된 주문코드가 입력되었습니다.</div>
                                    <div className="text-accect">
                                        {i + 2}번째 줄을 확인해주세요.
                                    </div>
                                </>
                            ),
                        })
                        return
                    }

                    if (!invoiceNumber) {
                        resolve({
                            isSuccess: false,
                            message: (
                                <>
                                    <div>송장번호가 누락되었습니다</div>
                                    <div className="text-accect">
                                        {i + 2}번째 줄을 확인해주세요.
                                    </div>
                                </>
                            ),
                        })
                        return
                    }
                })

                // console.log(filteredParsed);
                // console.log(reqData);
                console.log(5)

                const sendData = reqData.map((e) => {
                    const { orderCode, deliveryCompany, invoiceNumber } = e
                    return {
                        orderCode,
                        deliveryCompany,
                        invoiceNumber,
                    }
                })
                console.log(6)

                // 벌크 업로드 진행.
                invoiceUploadMutation.mutate(sendData, {
                    onSuccess: (res) => {
                        resolve({ isSuccess: true })
                    },
                    onError: (err: any) => {
                        resolve({
                            isSuccess: false,
                            message: `엑셀 업로드에 실패했습니다. (${err?.code})`,
                        })
                    },
                })
            }
        })
    }

    //
    const onOpenShpiingInfo = (item: IDetailTodo) => {
        setIsOpenShippingInfo(true)
        setUpdateItem(item)
    }

    const onCloseShippingInfo = () => {
        setIsOpenShippingInfo(false)

        setTimeout(() => {
            setUpdateItem(null)
        }, 500)
    }

    const onOpenInvoiceInfo = (guestId: number, invoice: IInvoiceBody | null) => {
        setIsOpenInvoiceInfo(true)
        setUpdateInvoiceItem({
            guestId,
            body: invoice,
        })
    }
    const onCloseInvoiceInfo = () => {
        setIsOpenInvoiceInfo(false)
        setTimeout(() => {
            setUpdateInvoiceItem(null)
        }, 500)
    }

    const value: Props = {
        // state
        tab,
        checked: Object.keys(checked),
        checkedOrder: checked,
        flag,
        updateItem,
        isResetFlag,
        updateInvoiceItem,
        isOpenShippingInfo,
        isOpenInvoiceInfo,

        // set
        setTab: _setTab,
        setChecked: _setChecked,

        // handler
        onAllChecked,
        onFlag,
        onExportExcel,
        onOpenShpiingInfo,
        onCloseShippingInfo,
        onOpenInvoiceInfo,
        onCloseInvoiceInfo,

        onInvoiceExcelUpload,
    }

    return <DetailContext.Provider value={value}>{children}</DetailContext.Provider>
}
